import type { DialogBreakpoints } from 'primevue/dialog';
import { pick } from 'lodash';

export type DialogPositionType =
    | 'center'
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'topleft'
    | 'topright'
    | 'bottomleft'
    | 'bottomright'
    | undefined;

export interface OpenDialogProps {
    modal?: boolean;
    closable?: boolean;
    maximizable?: boolean;
    dismissableMask?: boolean;
    closeOnEscape?: boolean;
    showHeader?: boolean;
    showFooter?: boolean;
    autoZIndex?: boolean;
    draggable?: boolean;
    keepInViewPort?: boolean;
    baseZIndex?: number;
    minX?: number;
    minY?: number;
    contentStyle?: any;
    contentClass?: any;
    position?: DialogPositionType;
    breakpoints?: DialogBreakpoints;
    style?: string | Record<string, string>;
    class?:
        | string
        | Array<string | Record<string, boolean>>
        | Record<string, boolean>;
}

export type { DialogBreakpoints };

export const dialogPropKeys: (keyof OpenDialogProps)[] = [
    'modal',
    'closable',
    'maximizable',
    'dismissableMask',
    'closeOnEscape',
    'showHeader',
    'showFooter',
    'autoZIndex',
    'draggable',
    'keepInViewPort',
    'baseZIndex',
    'minX',
    'minY',
    'contentStyle',
    'contentClass',
    'position',
    'breakpoints',
    'style',
    'class',
];

export function pickDialogProps<P extends OpenDialogProps = OpenDialogProps>(
    obj: P
): Pick<P, keyof OpenDialogProps> {
    return pick(obj, dialogPropKeys);
}

const dialogPropListenersConfig = {
    boolean: [
        'modal',
        'closable',
        'maximizable',
        'dismissableMask',
        'closeOnEscape',
        'draggable',
        'autoZIndex',
        'keepInViewPort',
    ],
    sections: { header: 'showHeader', footer: 'showFooter' },
    values: [
        'position',
        'breakpoints',
        'style',
        'class',
        'baseZIndex',
        'minX',
        'minY',
    ],
};

export const dialogPropListeners = (
    props: OpenDialogProps,
    defaultProps: OpenDialogProps = {}
) =>
    Object.fromEntries(
        dialogPropListenersConfig.boolean
            .concat(dialogPropListenersConfig.values)
            .concat(Object.values(dialogPropListenersConfig.sections))
            .map((k) => [
                `onDialog:${k}`,
                (value: unknown) => (props[k] = value ?? defaultProps[k]),
            ])
            .concat(
                dialogPropListenersConfig.boolean
                    .concat(Object.values(dialogPropListenersConfig.sections))
                    .map((k) => [
                        `onDialog:${k}:toggle`,
                        () => (props[k] = !props[k]),
                    ])
            )
            .concat(
                Object.entries(dialogPropListenersConfig.sections).flatMap(
                    ([k, v]) => [
                        [`${k}:show`, (value = true) => (props[v] = value)],
                        [`${k}:hide`, (value = true) => (props[v] = !value)],
                        [`${k}:toggle`, () => (props[v] = !props[v])],
                    ]
                )
            )
    );
